import {Injectable} from '@angular/core';

import {Observable, of, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import {Document, EmployeeDocument, TemplateAvailability} from './documents.interface';

import {ObserveType, ProfileMode, ResponseType} from '../../app.constants';
import {ETemplate} from '../../template-management/template-management.interfaces';
import {ApiService} from '../api';
import {Employee} from '../employees/employees.model';
import {FileService} from '../files/file.service';
import {
  FileDescription,
  FileStats,
  SignatureData,
  SignatureMethodEnum
} from '../files/files.model';
import {FormValuesWrapper} from '../form-generator/form-generator.interfaces';
import {FormGeneratorService} from '../form-generator/form-generator.service';
import {endOfDay} from '../utils/date';
import {FileModel, mapToFileModel} from '../utils/files';
import {escapeHtml, escapeValue, getInitials} from '../utils/strings';

export const documents_per_page = 10;
export const NOT_PUBLISHED_STATUS_FILTER = 'not-published';
export const PUBLISHED_STATUS_FILTER = 'published';

export interface SignatureMethodDto {
  name: SignatureMethodEnum;
  transId: string;
}

@Injectable()
export class DocumentsService {
  public refreshDocumentList$: Subject<boolean> = new Subject<boolean>();

  constructor(private api: ApiService,
              private fileService: FileService,
              private formGeneratorService: FormGeneratorService) {
    this.placeholderForEmployee = this.placeholderForEmployee.bind(this);
  }

  public fetchDocuments(contractId: number,
                        page = 0,
                        filter?: {
                          name: string,
                          template: string,
                          creator: string,
                          date: string,
                          statuses: Set<string>,
                          signatureStatuses?: Set<string>
                        },
                        perPage = documents_per_page,
                        availability = TemplateAvailability.HR_PRO,
                        profileMode: ProfileMode = ProfileMode.CLASSIC)
    : Observable<{ data: EmployeeDocument[], total: number, employeeDocumentCount?: number }> {
    let parameters: string = `page=${page}&per-page=${perPage}&order-by=-creationDate&search=type==EMPLOYEE_DOCUMENT`;

    if (filter?.date.trim().length > 0) {
      const fromDate = new Date(filter.date).toISOString();
      const toDate = endOfDay(new Date(filter.date), true).toISOString();
      parameters += `;(creationDate=gt=${fromDate};creationDate=le=${toDate})`;
    }

    if (filter?.statuses.size > 0) {
      const statuses = Array.from(filter.statuses);
      const publishedSelected = statuses.includes(PUBLISHED_STATUS_FILTER);
      const notPublishedSelected = statuses.includes(NOT_PUBLISHED_STATUS_FILTER);

      if (publishedSelected && !notPublishedSelected) {
        parameters += ';status=in=(SENT)';
      } else if (notPublishedSelected && !publishedSelected) {
        parameters += ';status=out=(SENT)';
      } else if (!publishedSelected && !notPublishedSelected) {
        parameters += `;status=in=(${statuses.join(',').toUpperCase()})`;
      }
    }

    if (filter?.signatureStatuses?.size > 0) {
      parameters += `;signatureStatus=in=(${Array.from(filter.signatureStatuses).join(',')})`;
    }

    if (filter?.name) {
      parameters += `;name=='*${escapeValue(filter.name)}*'`;
    }

    if (filter?.template) {
      parameters += `;(subFamily=='*${escapeValue(filter.template)}*',family=='*${escapeValue(filter.template)}*')`;
    }

    if (filter?.creator) {
      parameters += `&tags=creator=='*${escapeValue(filter.creator)}*'`;
    }

    return availability === TemplateAvailability.EMPLOYEE ?
      this.fileService.fetchMyFilesByContractId(contractId, parameters, false, profileMode)
      : this.fileService.fetchFilesByContractId(contractId, parameters, false, profileMode);
  }

  public uploadDocument(file: Blob, options: any, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<EmployeeDocument> {
    const formData: FormData = new FormData();
    const uploadOptions = Object.assign({}, {
      fileParamName: 'file',
      additionalData: {},
      headers: {}
    }, options);

    formData.append(
      uploadOptions.fileParamName,
      file,
      options.name
    );

    formData.append('body', new Blob([JSON.stringify({...options})], {type: 'application/json'}));

    return this.fileService.addFile(formData, options, true, profileMode);
  }

  public getDocument(documentId: number, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<FileModel> {
    return this.fileService.getFile(documentId, true, false, profileMode);
  }

  public deleteDocument(documentId: number, profileMode: ProfileMode = ProfileMode.CLASSIC) {
    return this.fileService.deleteFile(documentId, profileMode);
  }

  public initiateSignature(fileId: number, signatureData: SignatureData): Observable<SignatureData> {
    return this.fileService.initiateSignature(fileId, signatureData);
  }

  public checkSignature(fileId: number): Observable<FileDescription[]> {
    return this.fileService.checkSignature(fileId);
  }

  public getSignatureMethods(cliRegId: number): Observable<SignatureMethodDto[]> {
    return this.api.config.silent.get(`/regulation/${cliRegId}/signature-methods`);
  }

  public getTemplates(cliRegId: number, availability: TemplateAvailability): Observable<FormValuesWrapper> {
    return this.api.etemplate.silent.get(`/e-templates?cli-reg-id=${cliRegId}&availability=${availability}`)
      .pipe(map(data => this.formGeneratorService.transformTemplateArguments(data)));
  }

  public generateDocument(previewBody: Document): Observable<FileModel> {
    return this.api.etemplate.silent.post(`/e-templates/generate`, previewBody, {
        requestOptions: {
          observe: ObserveType.RESPONSE,
          responseType: ResponseType.BLOB
        }
      }
    ).pipe(
      map(mapToFileModel()),
      catchError((error) => error.status === 402 ? of(error) : of(null))
    );
  }

  public previewDocument(previewBody: Document): Observable<ETemplate> {
    return this.api.etemplate.silent.post(`/e-templates/preview`, previewBody);
  }

  public generateFromPreviewContent(previewBody: Document): Observable<FileModel> {
    return this.api.etemplate.silent.post(`/e-templates/generate-from-content`, previewBody, {
        requestOptions: {
          observe: ObserveType.RESPONSE,
          responseType: ResponseType.BLOB
        }
      }
    ).pipe(
      map(mapToFileModel()),
      catchError((error) => error.status === 402 ? of(error) : of(null))
    );
  }

  public getDocumentsStatsByContractId(contractId: number, type: string, family = ''): Observable<FileStats> {
    return this.fileService.getFilesStats(contractId, type, family, true);
  }

  public placeholderForEmployee(emp: Employee): string {
    const initials = getInitials(emp?.firstName, emp?.lastName);
    const photo = emp?.photoLink?.href
      ? `<span class="circle avatar w-24"><img alt="photo" src="${this.api.apiHost + emp?.photoLink?.href}" /></span>`
      : `<span class="gp-initials-avatar gp-avatar-xs">${initials}</span>`;
    return `<div class="flex align-items-center">
      ${photo}
      <span class="text-black">&nbsp;
        ${escapeHtml(emp?.fullName)}
      </span>
    </div>`;
  }
}
